import React from 'react';

import Locale from '../../Translate/Locale';
import Text from '../../common/Text';
import StatusBadge from '../../common/StatusBadge';
import PlainButton from '../../common/PlainButton';
import EventStatusBadge from '../EventStatusBadge';
import ClaimStatusBadge from '../ClaimStatusBadge/ClaimStatusBadge';
import ClaimTypeBadge from '../../claims-center/ClaimsTable/ClaimsTypeBadge';

import ViewIcon from '../../../icons/view.svg';

import styles from './ResultHeader.scss';
import { generateNameFromText } from '../../../slug';
import ClaimsTableSubmissionDate from '../../claims-center/ClaimsTable/ClaimsTableSubmissionDate';
import ConsumerActionStatusBadge from '../../claims-center/ClaimsTable/ConsumerActionStatusBadge';
import { ClaimStatus, ClaimType } from '../claims/types';
import { ConsumerActionStatus } from '../../../api/types';

interface LastEvent {
  meaning: string;
  type: string;
}

interface ResultHeaderProps {
  name: string;
  labelForId?: string;
  id?: string; // tracking code or shop order id
  lastEvent?: LastEvent;
  link?: string;
  children?: React.ReactNode;
  showDetailsButton?: boolean;
  claimStatus?: ClaimStatus;
  showClaimedTag?: boolean;
  showDeadline?: boolean;
  lastSubmissionDate?: string;
  claimSubmittedAt?: string;
  claimType?: ClaimType;
  claimId?: number;
  consumerActionStatus?: ConsumerActionStatus;
}

function ResultHeader(props: ResultHeaderProps): JSX.Element {
  const {
    name,
    labelForId,
    id,
    lastEvent,
    link,
    children,
    showDetailsButton,
    claimStatus,
    showClaimedTag,
    showDeadline,
    lastSubmissionDate,
    claimSubmittedAt,
    claimType,
    claimId,
    consumerActionStatus,
  } = props;
  const buttonName = generateNameFromText(`${name} view details`);

  return (
    <div className={styles.header}>
      <div className={styles.headerRow}>
        <StatusBadge published={false} neutral>
          {name}
        </StatusBadge>
        {id && (
          <React.Fragment>
            <Text className={styles.label}>
              <Locale>{labelForId}</Locale>:
            </Text>
            <Text className={styles.id} ink>
              {id}
            </Text>
          </React.Fragment>
        )}
        <div className={styles.details}>
          {children && <div className={styles.children}>{children}</div>}
          {showDetailsButton && (
            <a className={styles.link} href={link} target="_blank" rel="noreferrer">
              <PlainButton icon={ViewIcon} name={buttonName}>
                View details
              </PlainButton>
            </a>
          )}
        </div>
      </div>
      <div className={styles.statusRow}>
        {lastEvent && (
          <div className={styles.statusContainer}>
            <Text>
              <Locale>{`${name} status`}</Locale>
            </Text>
            <div className={styles.labeledBadge}>
              <EventStatusBadge meaning={lastEvent.meaning} type={lastEvent.type} />
            </div>
          </div>
        )}
        {showClaimedTag && (
          <div className={styles.statusContainer}>
            <Text>
              <Locale>Claim status</Locale>
            </Text>
            <div className={styles.labeledBadge}>
              <ClaimStatusBadge status={claimStatus} />
            </div>
            <Text>
              <Locale>Claim type</Locale>
            </Text>
            <div className={styles.labeledBadge}>
              <ClaimTypeBadge claimType={claimType} />
            </div>

            {showDeadline && claimStatus && claimId && (
              <>
                <Text>
                  <Locale>Deadline:</Locale>
                </Text>
                <div className={styles.labeledBadge}>
                  <ClaimsTableSubmissionDate
                    claim={{
                      deadlineLastSubmissionDate: lastSubmissionDate || null,
                      submittedAt: claimSubmittedAt || null,
                      status: claimStatus,
                      id: claimId,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {!!consumerActionStatus && (
          <div className={styles.statusContainer}>
            <Text>
              <Locale>Consumer Action</Locale>
            </Text>
            <div className={styles.labeledBadge}>
              <ConsumerActionStatusBadge consumerActionStatus={consumerActionStatus} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(ResultHeader);
